import type { App } from 'vue'
import type { GtagEventParams } from 'vue-gtag'
import type { Router } from 'vue-router'

import { createGtag, event } from 'vue-gtag'

import { useUserStore } from '@vben/stores'

const GTAG_ID = import.meta.env.VITE_GTAG_ID
let gtagRouter: null | Router = null

/**
 * GA 的通用扩展字段仅允许传递可安全序列化的基础值。
 *
 * `commonObj` 是当前 GA4 属性已配置的唯一业务扩展维度，业务域通过该字段
 * 承载低基数上下文，避免持续增加 GA 自定义维度配置成本。
 */
export type AnalyticsCommonObj = Record<
  string,
  boolean | number | string | undefined
>

/** 初始化全站 GA 客户端；业务域事件只能通过各自的上报模块进入。 */
export function setupGtag(app: App, router: Router) {
  if (!GTAG_ID) {
    return
  }
  gtagRouter = router
  app.use(
    createGtag({
      tagId: GTAG_ID,
      pageTracker: {
        router,
      },
    }),
  )
}

/**
 * 统一保持 `commonObj` 的 JSON 文本格式；序列化异常不能影响正常业务流程。
 */
export function serializeAnalyticsCommonObj(commonObj: AnalyticsCommonObj) {
  try {
    return JSON.stringify(commonObj)
  } catch {
    return '{}'
  }
}

/**
 * 使用当前 GA4 已配置的 `commonObj` 承载业务页面的低基数分析上下文。
 *
 * 业务事件不应各自处理 JSON 序列化，避免同一字段在不同模块出现不同格式，
 * 也避免为了少量页面行为不断新增 GA 自定义维度。
 */
export function reportAnalyticsCommonObjEvent(
  eventName: string,
  commonObj: AnalyticsCommonObj,
) {
  reportGtagEvent(eventName, {
    commonObj: serializeAnalyticsCommonObj(commonObj),
  } as GtagEventParams)
}

/**
 * 发送所有业务域共用的 GA 上下文。
 *
 * 埋点属于非关键链路，未初始化 Pinia 的单测、预渲染场景不能影响业务交互。
 */
export function reportGtagEvent(
  eventName: string,
  params: GtagEventParams = {},
) {
  let userName = ''

  try {
    const userStore = useUserStore()
    userName =
      userStore.userInfo?.username ?? userStore.userInfo?.realName ?? ''
  } catch {
    // 保留空用户名并继续发送其余上下文。
  }

  const commonParams = {
    route_path:
      gtagRouter?.currentRoute?.value?.fullPath ??
      window.location.pathname + window.location.search + window.location.hash,
    user_name: userName,
  }
  event(eventName, {
    ...commonParams,
    ...params,
  } as GtagEventParams)
}
